// Balance Scale generator. Each side has known weights and some number of mystery boxes (x).
// The scale balances when both sides weigh the same; the answer is the weight of one x.

import { int, type Rng } from '@kit/rng';

export interface Side { known: number[]; x: number }

export interface BalanceProblem {
  left: Side;
  right: Side;
  answer: number;
  /** 4 choices at the lower rungs; undefined means use the number pad */
  choices?: number[];
  /** symbol form, e.g. "x + 3 = 10" */
  symbols: string;
  /** plain-words form for the lower rungs */
  words: string;
  /** "?" at L1-4, "x" from L5 */
  label: '?' | 'x';
}

export function sideWeight(s: Side, x: number): number {
  return s.known.reduce((a, b) => a + b, 0) + s.x * x;
}

function sideText(s: Side, label: string): string {
  const parts: string[] = [];
  if (s.x === 1) parts.push(label); else if (s.x > 1) parts.push(`${s.x}${label}`);
  for (const k of s.known) parts.push(String(k));
  return parts.join(' + ') || '0';
}

function pickChoices(answer: number, rng: Rng): number[] {
  const set = new Set<number>([answer]);
  let guard = 0;
  while (set.size < 4 && guard++ < 50) {
    const v = answer + int(rng, -3, 3);
    if (v >= 1 && v !== answer) set.add(v);
  }
  while (set.size < 4) set.add(answer + set.size + 1);
  const arr = [...set];
  for (let i = arr.length - 1; i > 0; i--) { const j = Math.floor(rng() * (i + 1)); [arr[i], arr[j]] = [arr[j], arr[i]]; }
  return arr;
}

export function makeBalance(level: number, rng: Rng): BalanceProblem {
  const L = Math.max(1, Math.min(10, level));
  let left: Side, right: Side, answer: number;
  if (L <= 2) {
    answer = int(rng, 1, 10);
    left = { known: [answer], x: 0 }; right = { known: [], x: 1 };
  } else if (L <= 4) {
    answer = int(rng, 1, 12);
    const a = int(rng, 1, 9);
    left = { known: [answer + a], x: 0 }; right = { known: [a], x: 1 };
  } else if (L <= 6) {
    answer = int(rng, 2, 20);
    const a = int(rng, 1, 9);
    const total = answer + a;
    const b = int(rng, 1, total - 1);
    left = { known: [b, total - b], x: 0 }; right = { known: [a], x: 1 };
  } else if (L <= 8) {
    answer = int(rng, 2, 15);
    const a = int(rng, 1, 9);
    left = { known: [2 * answer + a], x: 0 }; right = { known: [a], x: 2 };
  } else {
    answer = int(rng, 2, 15);
    const a = int(rng, 1, 9);
    const n = int(rng, 2, 3);                 // n x on the right
    const b = (n - 1) * answer + a;           // nx + a = x + b
    left = { known: [b], x: 1 }; right = { known: [a], x: n };
  }
  // put the mystery on the right most of the time, sometimes swap for variety
  if (L >= 3 && rng() < 0.35) { const t = left; left = right; right = t; }
  const label: '?' | 'x' = L >= 5 ? 'x' : '?';
  const symbols = `${sideText(left, label)} = ${sideText(right, label)}`;
  const words = L <= 2
    ? `The mystery box weighs the same as ${answer === sideWeight(left, 0) ? sideWeight(left, 0) : sideWeight(right, 0)}.`
    : L <= 4
      ? `A mystery box plus ${Math.max(...[...left.known, ...right.known].filter((_, i, arr) => arr.length > 1 ? true : true))} balances the other side. How heavy is the box?`
      : 'Find the weight of x that makes both sides equal.';
  return { left, right, answer, choices: L <= 4 ? pickChoices(answer, rng) : undefined, symbols, words, label };
}

/** which way the beam tips for a trial value: negative = left heavier */
export function tilt(p: BalanceProblem, trial: number): number {
  return sideWeight(p.right, trial) - sideWeight(p.left, trial);
}
